import React, { useMemo, useState, useRef, useEffect } from 'react'
import { useNavigate, useNavigationType } from 'react-router-dom'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import { PanelSpecialIndent } from '../../ui_reusable/styles/PlacementMisc.styles'
import ClientProjectPicker from '../../comp_reusable/navigation/ClientProjectPicker'
import SelectableDriverList from '../../comp_reusable/lists/SelectableDriverList'
import BuildScenarioHeader from './build_scenario/BuildScenarioHeader'
import BuildScenarioPlotContainer from './build_scenario/BuildScenarioPlotContainer'
import BuildScenarioList from './build_scenario/BuildScenarioList'
import SwitchClientReportModal from './build_scenario/SwitchClientReportModal'
import ModelLoadingModal from '../../models/comp_reusable/LoadingModelModal'
import { useClientContext } from '../../../context/ClientContext'
import { useProjectContext } from '../../../context/ProjectContext'
import {
  selectedProjectAllowsModelRun,
  useClientProjectContext,
} from '../../../context/ClientProjectContext'
import useBuildScenarioStore from './stores/buildScenarioStore'
import ScenarioReportPayload from './utils/ScenarioReportPayload'

const BuildScenarioPage = () => {
  const { selectedClient } = useClientContext()
  const { selectedProject } = useProjectContext()
  const { clientProjectData } = useClientProjectContext()
  const canCreateScenarios = selectedProjectAllowsModelRun(
    selectedProject,
    clientProjectData,
  )
  const navigate = useNavigate()
  const navigationType = useNavigationType()

  // Store actions (no subscriptions for rendering)
  const setSelectedModel = useBuildScenarioStore(
    (state) => state.setSelectedModel,
  )
  const setSelectedDrivers = useBuildScenarioStore(
    (state) => state.setSelectedDrivers,
  )
  const resetScenarioTuningData = useBuildScenarioStore(
    (state) => state.resetScenarioTuningData,
  )
  const getSnapshot = useBuildScenarioStore((state) => state.getSnapshot)
  const prefill = useBuildScenarioStore((state) => state.prefill)
  const reset = useBuildScenarioStore((state) => state.reset)

  // Restore state ONLY when arriving via history navigation (back/forward button or navigate(-1))
  useEffect(() => {
    if (
      navigationType === 'POP' &&
      window.history.state?.buildScenarioSnapshot
    ) {
      prefill(window.history.state.buildScenarioSnapshot)
    } else if (navigationType !== 'POP') {
      reset()
    }
  }, [navigationType, prefill, reset])

  // Additional safety: Listen for popstate events to catch any missed history navigation
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state?.buildScenarioSnapshot) {
        prefill(event.state.buildScenarioSnapshot)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [prefill])

  // Local UI state only
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [loading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalResolver, setModalResolver] = useState(null)
  const [modalData, setModalData] = useState({ newValue: '', currentValue: '' })

  // Loading modal state for scenario API call
  const [isRunning, setIsRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const abortControllerRef = useRef(null)

  const handleClose = () => {
    setIsRunning(false)
    setProgress(0)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }

  // Subscribe only to data needed for driver selection
  const selectedDrivers = useBuildScenarioStore(
    (state) => state.selectedDrivers,
  )
  const driverData = useBuildScenarioStore((state) => state.drivers)
  const selectedModel = useBuildScenarioStore((state) => state.selectedModel)
  const driverDataForSelectableList = useMemo(() => {
    return driverData.map((driver) => ({
      driver: driver, // Actual driver object
      id: -1, // Unusable. Just adding it to match SelectableDriverList's expected format
    }))
  }, [driverData])

  const handleExpand = (dataIdx) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(dataIdx)) {
        next.delete(dataIdx)
      } else {
        next.add(dataIdx)
      }
      return next
    })
  }

  const handleDriverChange = (updatedSelectedDrivers) => {
    setSelectedDrivers(updatedSelectedDrivers)
  }

  const handleModelSelect = (model) => {
    setSelectedModel(model)
    setExpandedRows(new Set()) // Reset expanded rows
  }

  const handleReset = () => {
    resetScenarioTuningData()
    setExpandedRows(new Set())
  }

  const handleSubmit = async () => {
    if (!canCreateScenarios) {
      return
    }
    // Start loading state
    setIsRunning(true)
    let currentProgress = 0
    const interval = setInterval(() => {
      currentProgress += 10
      setProgress(currentProgress)
      if (currentProgress >= 90) clearInterval(interval) // Stop near completion
    }, 300)

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create a new AbortController
    abortControllerRef.current = new AbortController()

    try {
      // Get snapshot before processing to preserve state
      const snapshot = getSnapshot()

      const scenarioData = ScenarioReportPayload(snapshot)
      if (scenarioData) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/scenarios/generate_report`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({
              artifact_id: snapshot.selectedModel.id,
              ...scenarioData,
            }),
          },
        )
        const result = await response.json()
        const data = {
          ...result.data,
          scenario: scenarioData,
        }
        setProgress(100)

        setTimeout(() => {
          setIsRunning(false)

          // Get a fresh snapshot before navigating
          const freshSnapshot = getSnapshot()

          // Save the current state to browser history before navigating
          // This will be available when user navigates back via history
          window.history.replaceState(
            { ...window.history.state, buildScenarioSnapshot: freshSnapshot },
            '',
          )

          // Navigate to scenario report page
          navigate('/scenarios/report', {
            state: {
              data: data,
            },
          })
        }, 500)
      }
    } catch (error) {
      if (error.name === 'AbortError') {
      } else {
        console.error('Unexpected error:', error)
        setIsRunning(false)
      }
    }

    clearInterval(interval)
    setIsRunning(false)
  }

  // Intercept click handler for ClientProjectPicker
  // This is to prevent the user from accidentally switching clients/projects and losing their scenario work
  const handleClientProjectInterceptClick = async (
    value,
    label,
    selectedValue,
    data,
  ) => {
    // If no model is selected, no need to check - just proceed
    const currentSelectedModel = selectedModel
    if (!currentSelectedModel) {
      return true
    }

    // Parse the new client/project from the value
    if (!value) {
      // "Select all" was chosen, proceed without modal
      return true
    }

    const [newClientId, newProjectId] = value.split('-')
    const newClientIdInt = parseInt(newClientId)
    const newProjectIdInt = parseInt(newProjectId)

    // If the client/project isn't actually changing, no need for modal
    if (
      selectedClient?.id === newClientIdInt &&
      selectedProject?.id === newProjectIdInt
    ) {
      return true
    }

    // Check if the current model will be available in the new client/project.
    // Full-list membership check (not a table); bounded server-side at 9999
    // (BaseCitrusPagination max_page_size).
    try {
      let url = `${process.env.REACT_APP_API_URL}/api/v2/artifacts/?client_id=${newClientId}&project_id=${newProjectId}`

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      if (response.ok) {
        const result = await response.json()
        const availableModels = result.data || []

        // Check if current model exists in the new client/project
        const modelExists = availableModels.some(
          (model) => model.id === currentSelectedModel.id,
        )

        if (modelExists) {
          // Model exists, no need for modal
          return true
        } else {
          // Model doesn't exist, show modal
          return new Promise((resolve) => {
            const currentLabel =
              data.find((item) => item.value === selectedValue)?.label || 'None'
            setModalData({ newValue: label, currentValue: currentLabel })
            setModalResolver(() => resolve)
            setModalOpen(true)
          })
        }
      } else {
        // API error, show modal to be safe
        return new Promise((resolve) => {
          const currentLabel =
            data.find((item) => item.value === selectedValue)?.label || 'None'
          setModalData({ newValue: label, currentValue: currentLabel })
          setModalResolver(() => resolve)
          setModalOpen(true)
        })
      }
    } catch (error) {
      console.error('Error checking model availability:', error)
      // On error, show modal to be safe
      return new Promise((resolve) => {
        const currentLabel =
          data.find((item) => item.value === selectedValue)?.label || 'None'
        setModalData({ newValue: label, currentValue: currentLabel })
        setModalResolver(() => resolve)
        setModalOpen(true)
      })
    }
  }

  const handleClientProjectModalConfirm = () => {
    if (modalResolver) {
      modalResolver(true)
    }
    setModalOpen(false)
    setModalResolver(null)
  }

  const handleClientProjectModalClose = () => {
    if (modalResolver) {
      modalResolver(false)
    }
    setModalOpen(false)
    setModalResolver(null)
  }

  return (
    <>
      <HeaderContainer>
        <SectionTitle>Build Scenario</SectionTitle>
        <ClientProjectPicker
          interceptClickWaitOnConfirm={handleClientProjectInterceptClick}
        />
      </HeaderContainer>

      <SimpleAccordion
        id="buildScenarioPageAccordion"
        defaultActiveKey={[1, 2, 3]}
      >
        <SimpleAccordion.Panel header="Select model" eventKey={1}>
          <PanelSpecialIndent>
            <BuildScenarioHeader
              selectedClient={selectedClient}
              selectedProject={selectedProject}
              onModelSelect={handleModelSelect}
              selectedModel={selectedModel}
            />
          </PanelSpecialIndent>
        </SimpleAccordion.Panel>

        <SimpleAccordion.Panel header="Select drivers" eventKey={2}>
          <SelectableDriverList
            data={driverDataForSelectableList}
            loading={loading}
            selectedDrivers={selectedDrivers}
            dependentDriverId={null}
            expandedRows={expandedRows}
            onExpandRow={handleExpand}
            totalAvailable={driverDataForSelectableList.length}
            totalSelected={selectedDrivers.length}
            onSelectedChange={handleDriverChange}
            stashedDrivers={[]}
            useRole={false}
          />
        </SimpleAccordion.Panel>

        <SimpleAccordion.Panel header="Create scenario" eventKey={3}>
          <BuildScenarioPlotContainer selectedModel={selectedModel} />
          <BuildScenarioList
            onSubmit={handleSubmit}
            onReset={handleReset}
            canCreateScenarios={canCreateScenarios}
          />
        </SimpleAccordion.Panel>
      </SimpleAccordion>

      <SwitchClientReportModal
        open={modalOpen}
        onClose={handleClientProjectModalClose}
        onConfirm={handleClientProjectModalConfirm}
        newValue={modalData.newValue}
        currentValue={modalData.currentValue}
      />

      <ModelLoadingModal
        open={isRunning}
        progress={progress}
        handleClose={handleClose}
      />
    </>
  )
}

export default BuildScenarioPage
